import { useLanguage } from '@/contexts/LanguageContext';
import { useSearchParams, Link } from 'react-router-dom';
import { Shield, CheckCircle, Clock, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function BackgroundCheck() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role') || 'consultant';

  const translations = {
    en: {
      title: 'Background Check Required',
      subtitle: 'Your application has been submitted successfully',
      description: 'As part of our verification process, a background check will be conducted to ensure the integrity and trust of our platform.',
      steps: {
        title: 'What happens next?',
      step1: {
          title: 'Application Review',
          description: 'Our team will review your application.'
        },
        step2: {
          title: 'Background Verification',
          description: 'A comprehensive background check will be conducted by our trusted partner using Verify.'
        },
        step3: {
          title: 'Email Confirmation',
          description: 'You will receive an email notification once your verification is complete.'
        },
        step4: {
          title: 'Complete Payment',
          description: 'After approval, you can proceed to complete your registration payment.'
        }
      },
      notice: 'The background check verification typically takes a few minutes with Verify. Please check your email for updates.',
      emailNotice: 'A confirmation email has been sent to your registered email address.',
      backHome: 'Return to Home',
      questions: 'Have questions?',
      contactUs: 'Contact our support team'
    },
    fr: {
      title: 'Vérification des antécédents requise',
      subtitle: 'Votre demande a été soumise avec succès',
      description: 'Dans le cadre de notre processus de vérification, une vérification des antécédents sera effectuée pour assurer l\'intégrité et la confiance de notre plateforme.',
      steps: {
        title: 'Quelles sont les prochaines étapes ?',
      step1: {
          title: 'Examen de la demande',
          description: 'Notre équipe examinera votre demande.'
        },
        step2: {
          title: 'Vérification des antécédents',
          description: 'Une vérification complète des antécédents sera effectuée par notre partenaire de confiance via Verify.'
        },
        step3: {
          title: 'Confirmation par courriel',
          description: 'Vous recevrez une notification par courriel une fois la vérification terminée.'
        },
        step4: {
          title: 'Compléter le paiement',
          description: 'Après approbation, vous pourrez procéder au paiement de votre inscription.'
        }
      },
      notice: 'La vérification des antécédents prend généralement quelques minutes avec Verify. Veuillez consulter votre courriel pour les mises à jour.',
      emailNotice: 'Un courriel de confirmation a été envoyé à votre adresse électronique enregistrée.',
      backHome: 'Retour à l\'accueil',
      questions: 'Des questions ?',
      contactUs: 'Contactez notre équipe de support'
    }
  };

  const t = translations[language];

  const roleLabels = {
    consultant: language === 'fr' ? 'Consultant' : 'Consultant',
    lender: language === 'fr' ? 'Prêteur' : 'Lender',
    partner: language === 'fr' ? 'Partenaire Interne' : 'Internal Partner'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4">
      {/* Logo */}
      <div className="flex justify-center mb-6">
        <a href="/">
          <img src="/images/acquizgroup-logo.png" alt="AcquizGroup" className="h-12 object-contain" />
        </a>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Header Card */}
        <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] rounded-t-xl p-8 text-center shadow-lg">
          <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-10 w-10 text-[#CEAB65]" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {t.title}
          </h1>
          <p className="text-white/80">
            {roleLabels[role as keyof typeof roleLabels] || role}
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-white border border-slate-200 rounded-b-xl p-6 md:p-8 shadow-lg">
          {/* Success Message */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-[#1D314D] mb-2">{t.subtitle}</h2>
            <p className="text-slate-600">{t.description}</p>
          </div>

          {/* Steps */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-[#1D314D] mb-4">{t.steps.title}</h3>
            <div className="space-y-4">
              {[t.steps.step1, t.steps.step2, t.steps.step3, t.steps.step4].map((step, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#1D314D] text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-medium text-[#1D314D]">{step.title}</h4>
                    <p className="text-sm text-slate-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm">{t.notice}</p>
            </div>
          </div>

          {/* Email Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-blue-800 text-sm">{t.emailNotice}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild variant="outline" className="gap-2">
              <Link to="/">
                <ArrowLeft className="h-4 w-4" />
                {t.backHome}
              </Link>
            </Button>
            <Button asChild className="bg-[#1D314D] hover:bg-[#2C3E50] gap-2">
              <Link to="/contact">
                {t.contactUs}
              </Link>
            </Button>
          </div>

          {/* Questions */}
          <p className="text-center text-slate-500 text-sm mt-6">
            {t.questions}{' '}
            <Link to="/contact" className="text-[#1D314D] hover:text-[#CEAB65] font-medium">
              {t.contactUs}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
